// EnhancedCitationDisplay.js
// Enhanced citation display component with metadata, grouping, and real-time updates

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  SpaceBetween,
  Badge,
  Button,
  Icon,
  TextContent,
  ProgressBar,
  Container,
  Alert,
  ExpandableSection,
  Header
} from '@cloudscape-design/components';
import CitationService from '../../services/CitationService';
import './EnhancedCitationDisplay.css';

/**
 * EnhancedCitationDisplay - Main component for displaying citations with enhanced metadata
 */
const EnhancedCitationDisplay = ({ 
  citations = [], 
  messageId, 
  onCitationClick,
  isLoading = false,
  hasBackgroundProcessing = false,
  isAgentComplete = false // New prop to track if agent has finished
}) => {
  const [processedData, setProcessedData] = useState(null);
  const [loading, setLoading] = useState(isLoading);
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [enhancedDataAvailable, setEnhancedDataAvailable] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('idle');

  // Check for enhanced data from background processing
  const checkForEnhancedData = useCallback(async () => {
    if (!messageId) return;

    try {
      const enhancedMetadata = await CitationService.fetchEnhancedMetadata(messageId);
      
      if (enhancedMetadata && enhancedMetadata.citations) {
        console.log('🎉 Enhanced citations received from background processing');
        const mergedData = await CitationService.mergeEnhancedCitations(
          messageId, 
          enhancedMetadata.citations
        );
        
        setProcessedData(mergedData);
        setEnhancedDataAvailable(true);
        setProcessingStatus('complete');
      }
    } catch (error) {
      console.error('Error fetching enhanced metadata:', error);
      setProcessingStatus('error');
    }
  }, [messageId]);

  // Process citations when they change
  useEffect(() => {
    const processCitations = async () => {
      if (citations && citations.length > 0 && messageId) {
        setLoading(true);
        try {
          const processed = await CitationService.processCitations(citations, messageId, false);
          setProcessedData(processed);
          
          // Check if enhanced data is available
          if (hasBackgroundProcessing) {
            setProcessingStatus('processing');
            checkForEnhancedData();
          }
        } catch (error) {
          console.error('Error processing citations:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setProcessedData(null);
        setLoading(false);
      }
    };

    processCitations();
  }, [citations, messageId, hasBackgroundProcessing, checkForEnhancedData]);

  // Register for enhanced citations callback
  useEffect(() => {
    if (messageId && hasBackgroundProcessing) {
      CitationService.onEnhancedCitationsReady(messageId, (enhancedData) => {
        console.log('🎉 Enhanced citations ready via callback');
        setProcessedData(enhancedData);
        setEnhancedDataAvailable(true);
        setProcessingStatus('complete');
      });
    }
  }, [messageId, hasBackgroundProcessing]);

  if (loading) {
    return (
      <Container>
        <Box padding="s">
          <ProgressBar 
            status="in-progress" 
            label="Processing citations..." 
            description="Analyzing sources and extracting metadata"
          />
        </Box>
      </Container>
    );
  }

  // Don't show citations until agent is complete
  if (!isAgentComplete || !processedData || processedData.citations.length === 0) {
    return null;
  }

  return (
    <Container>
      <ExpandableSection
        headerText={`Sources and Citations (${processedData.citations.length})`}
        expanded={isExpanded}
        onChange={({ detail }) => setIsExpanded(detail.expanded)}
        headerDescription={`${processedData.summary.uniqueDataSources} unique sources found`}
        headerActions={
          <SpaceBetween direction="horizontal" size="s">
            <Badge color="blue">
              {processedData.summary.total} Sources
            </Badge>
            {processedData.summary.averageConfidence > 0 && (
              <Badge color={processedData.summary.averageConfidence >= 0.8 ? 'green' : processedData.summary.averageConfidence >= 0.5 ? 'blue' : 'grey'}>
                {Math.round(processedData.summary.averageConfidence * 100)}% Avg Confidence
              </Badge>
            )}
            {enhancedDataAvailable && (
              <Badge color="green">
                <Icon name="status-positive" size="small" /> Enhanced
              </Badge>
            )}
            {processingStatus === 'processing' && (
              <Badge color="blue">
                <Icon name="status-in-progress" size="small" /> Processing
              </Badge>
            )}
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          {/* Processing Status Alert */}
          {hasBackgroundProcessing && processingStatus === 'processing' && (
            <Alert
              header="Enhanced Processing in Progress"
            >
              We're analyzing sources in the background to provide additional metadata and insights. 
              This page will update automatically when complete.
            </Alert>
          )}

          {/* Enhanced Data Available Alert */}
          {enhancedDataAvailable && (
            <Alert
              type="success"
              header="Enhanced Citations Available"
              dismissible
            >
              Citations have been enhanced with additional metadata, content analysis, and source information.
            </Alert>
          )}

          {/* Single Citation List - All Sources */}
          <CitationList 
            citations={processedData.citations} 
            onCitationClick={onCitationClick} 
          />
        </SpaceBetween>
      </ExpandableSection>
    </Container>
  );
};



/**
 * CitationList - List of citations for a specific tab
 */
const CitationList = ({ citations, onCitationClick }) => {
  if (citations.length === 0) {
    return (
      <Box textAlign="center" padding="l">
        <TextContent>
          <p>No citations available in this category.</p>
        </TextContent>
      </Box>
    );
  }

  return (
    <SpaceBetween size="s">
      {citations.map((citation, index) => (
        <EnhancedCitation
          key={citation.id || index}
          citation={citation}
          onClick={() => onCitationClick && onCitationClick(citation)}
        />
      ))}
    </SpaceBetween>
  );
};

/**
 * EnhancedCitation - Individual citation component with metadata
 */
const EnhancedCitation = ({ citation, onClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpand = (expanded) => {
    setIsExpanded(expanded);
  };

  return (
    <Container>
      <SpaceBetween size="s">
        {/* Citation Header */}
        <Box>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Icon name={citation.icon} />
            <Badge color={citation.color}>
              {citation.sourceType}
            </Badge>
            <TextContent>
              <strong>{citation.displayName}</strong>
            </TextContent>
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                {citation.confidence && (
                  <Badge color={citation.relevanceLevel === 'high' ? 'green' : citation.relevanceLevel === 'medium' ? 'blue' : 'grey'}>
                    {Math.round(citation.confidence * 100)}%
                  </Badge>
                )}
                {citation.isEnhanced && (
                  <Badge color="green">
                    <Icon name="status-positive" size="small" /> Enhanced
                  </Badge>
                )}
              </SpaceBetween>
            </Box>
          </SpaceBetween>
        </Box>

        {/* Citation Content Preview */}
        {citation.content && (
          <Box>
            <TextContent>
              <p style={{ 
                fontSize: '0.9em', 
                color: '#687078',
                fontStyle: 'italic',
                lineHeight: '1.4'
              }}>
                "{citation.content.length > 200 ? citation.content.substring(0, 200) + '...' : citation.content}"
              </p>
            </TextContent>
          </Box>
        )}

        {/* Enhanced Metadata (Expandable) */}
        {(citation.hasMetadata || citation.hasContentAnalysis) && (
          <ExpandableSection
            headerText="Additional Information"
            expanded={isExpanded}
            onChange={({ detail }) => handleExpand(detail.expanded)}
          >
            <SpaceBetween size="s">
              {/* Content Analysis */}
              {citation.contentAnalysis && (
                <ContentAnalysisDisplay analysis={citation.contentAnalysis} />
              )}
              
              {/* Document Metadata */}
              {citation.documentMetadata && (
                <DocumentMetadataDisplay metadata={citation.documentMetadata} />
              )}
              
              {/* Raw Metadata */}
              {citation.metadata && Object.keys(citation.metadata).length > 0 && (
                <MetadataDisplay metadata={citation.metadata} />
              )}
            </SpaceBetween>
          </ExpandableSection>
        )}

        {/* Citation Actions */}
        <CitationActions citation={citation} onClick={onClick} />
      </SpaceBetween>
    </Container>
  );
};

/**
 * ContentAnalysisDisplay - Display content analysis information
 */
const ContentAnalysisDisplay = ({ analysis }) => {
  return (
    <Box>
      <Header variant="h5">Content Analysis</Header>
      <SpaceBetween size="xs">
        <SpaceBetween direction="horizontal" size="s">
          <Badge>{analysis.wordCount} words</Badge>
          <Badge>{analysis.characterCount} characters</Badge>
          {analysis.language && <Badge>Language: {analysis.language}</Badge>}
          {analysis.technicalLevel && (
            <Badge color={analysis.technicalLevel === 'high' ? 'red' : analysis.technicalLevel === 'medium' ? 'blue' : 'green'}>
              {analysis.technicalLevel} technical level
            </Badge>
          )}
        </SpaceBetween>
        
        {analysis.topics && analysis.topics.length > 0 && (
          <Box>
            <TextContent>
              <strong>Topics:</strong> {analysis.topics.join(', ')}
            </TextContent>
          </Box>
        )}
        
        {analysis.sentiment && (
          <Box>
            <TextContent>
              <strong>Sentiment:</strong> 
              <Badge color={analysis.sentiment === 'positive' ? 'green' : analysis.sentiment === 'negative' ? 'red' : 'grey'}>
                {analysis.sentiment}
              </Badge>
            </TextContent>
          </Box>
        )}
        
        <SpaceBetween direction="horizontal" size="xs">
          {analysis.hasNumbers && <Badge color="blue">Contains Numbers</Badge>}
          {analysis.hasUrls && <Badge color="purple">Contains URLs</Badge>}
          {analysis.hasStructuredData && <Badge color="green">Structured Content</Badge>}
        </SpaceBetween>
      </SpaceBetween>
    </Box>
  );
};

/**
 * DocumentMetadataDisplay - Display document-specific metadata
 */
const DocumentMetadataDisplay = ({ metadata }) => {
  return (
    <Box>
      <Header variant="h5">Document Information</Header>
      <SpaceBetween size="xs">
        {metadata.title && (
          <TextContent>
            <strong>Title:</strong> {metadata.title}
          </TextContent>
        )}
        
        {metadata.category && (
          <TextContent>
            <strong>Category:</strong> 
            <Badge>{metadata.category}</Badge>
          </TextContent>
        )}
        
        {metadata.documentDate && (
          <TextContent>
            <strong>Date:</strong> {metadata.documentDate}
          </TextContent>
        )}
        
        {metadata.version && (
          <TextContent>
            <strong>Version:</strong> {metadata.version}
          </TextContent>
        )}
        
        {metadata.readabilityScore && (
          <TextContent>
            <strong>Readability Score:</strong> 
            <Badge color={metadata.readabilityScore >= 70 ? 'green' : metadata.readabilityScore >= 40 ? 'blue' : 'red'}>
              {Math.round(metadata.readabilityScore)}/100
            </Badge>
          </TextContent>
        )}
      </SpaceBetween>
    </Box>
  );
};

/**
 * MetadataDisplay - Display raw metadata in expandable format
 */
const MetadataDisplay = ({ metadata }) => {
  return (
    <Box>
      <Header variant="h5">Technical Metadata</Header>
      <Box padding="xs">
        <pre style={{ 
          fontSize: '0.8em', 
          background: '#f8f9fa', 
          padding: '8px',
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '200px'
        }}>
          {JSON.stringify(metadata, null, 2)}
        </pre>
      </Box>
    </Box>
  );
};

/**
 * CitationActions - Action buttons for citations
 */
const CitationActions = ({ citation, onClick }) => {
  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(citation.content || '');
      console.log('Citation content copied to clipboard');
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  const handleViewSource = () => {
    if (citation.location?.uri) {
      window.open(citation.location.uri, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <SpaceBetween direction="horizontal" size="xs">
      {citation.content && (
        <Button
          variant="link"
          iconName="copy"
          onClick={handleCopyContent}
        >
          Copy Content
        </Button>
      )}
      
      {citation.location?.uri && (
        <Button
          variant="link"
          iconName="external"
          onClick={handleViewSource}
        >
          View Source
        </Button>
      )}
      

    </SpaceBetween>
  );
};

export default EnhancedCitationDisplay;